import classNames from 'classnames'
import React, { PropsWithChildren } from 'react'

import { Body1, Body2, Caption1 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'
import { Button, ButtonProps } from '@ui/components/Button/Button'
import { IconButton, IconButtonProps } from '@ui/components/Button/IconButton'
import { Checkbox, CheckboxProps } from '@ui/components/Checkbox'
import { Link } from '@ui/components/Link/Link'
import { RadioIndicator, RadioIndicatorProps } from '@ui/components/Radio'
import { RichIcon, RichIconProps } from '@ui/components/RichIcon'
import { Toggle, ToggleProps } from '@ui/components/Toggle/Toggle'
import { TextColorType } from '@ui/design-system/colors/colors'

import { Box } from '@shared/components/Box'

import styles from './ListItem.module.scss'

type Padding = 'default' | 'vertical'

export type ListItemVariant = 'md' | 'lg'

type OneOf<T> = {
  [K in keyof T]: {
    [P in K]: T[P]
  } & {
    [P in Exclude<keyof T, K>]?: never
  }
}[keyof T]

type EndSlot = OneOf<{
  checkbox: CheckboxProps | true
  radio: RadioIndicatorProps | true
  button: ButtonProps
  iconButtons: IconButtonProps[]
  toggle: ToggleProps
  icon: RichIconProps
}>

type ListItemLink = {
  href: string
  target?: React.HTMLAttributeAnchorTarget
}

type SubtitleLink = ListItemLink & {
  text: string
}

type SubtitleItem = string | { text: string; color?: TextColorType; italic?: boolean }
type Subtitle = SubtitleItem | SubtitleItem[] | SubtitleLink

const isSubtitleLink = (subtitle: Subtitle): subtitle is SubtitleLink =>
  typeof subtitle === 'object' && !Array.isArray(subtitle) && 'href' in subtitle

const normalizeSubtitle = (
  subtitle: SubtitleItem | SubtitleItem[]
): { text: string; color: TextColorType; italic?: boolean }[] =>
  (Array.isArray(subtitle) ? subtitle : [subtitle]).map((item) =>
    typeof item === 'string'
      ? { text: item, color: 'text-med-em' }
      : { text: item.text, color: item.color ?? 'text-med-em', italic: item.italic }
  )

type TitleSlot = { badgeText: string } | { node: React.ReactNode } | RichIconProps

interface ListItemCommonProps {
  title?: string
  subtitle?: Subtitle
  startSlot?: RichIconProps
  endSlot?: EndSlot
  description?: {
    title?: string
    subtitle?: string
    sensitive?: boolean
    whiteSpace?: React.CSSProperties['whiteSpace']
  }
  inverted?: boolean
  titleSlot?: TitleSlot
  subtitleSlot?: TitleSlot
  size?: ListItemVariant
  onClick?: () => void
  active?: boolean
  disabled?: boolean
  header?: boolean
  mobileHoverDisabled?: boolean
  hoverDisabled?: boolean
  children?: React.ReactNode
  hint?: {
    text: string
    icon?: RichIconProps
  }
  padding?: Padding
  'data-tour-target'?: string
}

interface ListItemDefaultProps extends ListItemCommonProps {
  as?: 'div'
  link?: ListItemLink
}

interface ListItemButtonProps
  extends ListItemCommonProps,
    Pick<React.AriaAttributes, 'aria-label' | 'aria-current' | 'aria-busy'> {
  as: 'button'
  link?: never
  ref?: React.Ref<HTMLButtonElement>
  role?: 'menuitem'
  tabIndex?: number
}

export type ListItemProps = ListItemDefaultProps | ListItemButtonProps

export const ListItem: React.FC<PropsWithChildren<ListItemProps>> = (props) => {
  const {
    link,
    title,
    subtitle,
    startSlot,
    endSlot,
    description,
    inverted,
    titleSlot,
    subtitleSlot,
    children,
    active,
    disabled,
    onClick,
    header,
    size = 'md',
    mobileHoverDisabled,
    hoverDisabled,
    hint,
    padding = 'default',
    'data-tour-target': tourTarget,
  } = props

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick()
    }
  }

  const getIconSize = () => {
    switch (size) {
      case 'md':
        return 'x20'
      default:
        return 'x24'
    }
  }

  const classes = classNames(styles.listItem, styles[`size-${size}`], {
    [styles.stateActive]: active,
    [styles.stateDisabled]: disabled,
    [styles.stateHeader]: header,
    [styles.mobileHoverDisabled]: mobileHoverDisabled,
    [styles.hoverDisabled]: hoverDisabled,
    [styles.paddingVertical]: padding === 'vertical',
  })

  const renderEndSlotElement = (endSlot: EndSlot) => {
    if ('checkbox' in endSlot) {
      const checkboxProps = endSlot.checkbox === true ? {} : endSlot.checkbox
      return (
        <Checkbox
          {...checkboxProps}
          checked={active}
          disabled={!!disabled}
          onChange={() => {
            onClick?.()
          }}
        />
      )
    }

    if ('radio' in endSlot) {
      const radioProps = endSlot.radio === true ? {} : endSlot.radio
      return <RadioIndicator {...radioProps} checked={active} disabled={!!disabled} size='md' />
    }

    if ('button' in endSlot) {
      return <Button {...endSlot.button} />
    }

    if ('toggle' in endSlot) {
      return <Toggle {...(endSlot.toggle as ToggleProps)} />
    }

    if ('icon' in endSlot) {
      return <RichIcon {...endSlot.icon} size={endSlot.icon?.size ?? getIconSize()} />
    }

    if ('iconButtons' in endSlot) {
      return (
        <Box flex align='center' justify='center' gap='x4'>
          {endSlot.iconButtons.map((iconButtonProps, index) => (
            <IconButton key={index} {...(iconButtonProps as IconButtonProps)} />
          ))}
        </Box>
      )
    }
  }

  const renderTitleSlot = (titleSlot: TitleSlot) => {
    if ('badgeText' in titleSlot) {
      return (
        <Badge type='purple' contrast='low' size='xxs'>
          {titleSlot.badgeText}
        </Badge>
      )
    }

    if ('node' in titleSlot) {
      return <>{titleSlot.node}</>
    }

    return <RichIcon {...(titleSlot as RichIconProps)} />
  }

  const content = (
    <>
      <Box flex align='center' gap='x12' fullWidth>
        {startSlot && <RichIcon {...startSlot} size={startSlot.size ?? getIconSize()} />}
        <Box
          flex
          direction='column'
          align='flex-start'
          flexItem='1 0 0'
          className={classNames({ [styles.inverted]: inverted })}
        >
          {size === 'lg' ? (
            <Box flex align='center' gap='x4'>
              <Body1 as='div' className={styles.title} medium color='text-high-em'>
                {title ?? children}
              </Body1>
              {titleSlot && renderTitleSlot(titleSlot)}
            </Box>
          ) : (
            <Box flex align='center' gap='x4'>
              <Body2 as='div' className={styles.title} medium color='text-high-em'>
                {title ?? children}
              </Body2>
              {titleSlot && renderTitleSlot(titleSlot)}
            </Box>
          )}
          {subtitle &&
            (isSubtitleLink(subtitle) ? (
              <Link
                href={subtitle.href}
                target={subtitle.target}
                size='md'
                variant='tertiary'
                onClick={(e) => e.stopPropagation()}
              >
                {size === 'lg' ? (
                  <Body2 className={styles.subtitle} color='text-med-em'>
                    {subtitle.text}
                  </Body2>
                ) : (
                  <Caption1 className={styles.subtitle} color='text-med-em'>
                    {subtitle.text}
                  </Caption1>
                )}
              </Link>
            ) : (
              normalizeSubtitle(subtitle).map((item, index) =>
                size === 'lg' ? (
                  <Body2
                    key={index}
                    className={classNames(styles.subtitle, {
                      [styles.subtitleItalic]: item.italic,
                    })}
                    color={item.color}
                  >
                    {item.text}
                  </Body2>
                ) : (
                  <Caption1
                    key={index}
                    className={classNames(styles.subtitle, {
                      [styles.subtitleItalic]: item.italic,
                    })}
                    color={item.color}
                  >
                    {item.text}
                  </Caption1>
                )
              )
            ))}
          {subtitleSlot && (
            <Box pt='x4' className={styles.subtitleSlot}>
              {renderTitleSlot(subtitleSlot)}
            </Box>
          )}
        </Box>
        {description && (
          <Box
            flex
            direction='column'
            align='flex-end'
            className={classNames({ [styles.inverted]: inverted })}
          >
            {description.title &&
              (size === 'lg' ? (
                <Body1
                  className={styles.descriptionTitle}
                  color='text-high-em'
                  sensitive={description.sensitive}
                  whiteSpace={description.whiteSpace}
                >
                  {description.title}
                </Body1>
              ) : (
                <Body2
                  className={styles.descriptionTitle}
                  color='text-high-em'
                  sensitive={description.sensitive}
                  whiteSpace={description.whiteSpace}
                >
                  {description.title}
                </Body2>
              ))}
            {description.subtitle &&
              (size === 'lg' ? (
                <Body2 className={styles.descriptionSubtitle} color='text-med-em'>
                  {description.subtitle}
                </Body2>
              ) : (
                <Caption1 className={styles.descriptionSubtitle} color='text-med-em'>
                  {description.subtitle}
                </Caption1>
              ))}
          </Box>
        )}
        {endSlot && renderEndSlotElement(endSlot)}
      </Box>

      {hint && (
        <Box flex align='center' gap='x4' px='x8' pb='x8' fullWidth>
          {hint.icon && <RichIcon {...hint.icon} size={hint.icon.size ?? getIconSize()} />}
          <Caption1 color='text-med-em'>{hint.text}</Caption1>
        </Box>
      )}
    </>
  )

  const element =
    props.as === 'button' ? (
      <button
        ref={props.ref}
        type='button'
        role={props.role}
        tabIndex={props.tabIndex}
        className={classNames(classes, styles.button)}
        aria-label={props['aria-label']}
        aria-current={props['aria-current']}
        aria-busy={props['aria-busy']}
        disabled={disabled}
        onClick={handleClick}
        data-tour-target={tourTarget}
      >
        {content}
      </button>
    ) : (
      <Box
        flex
        direction='column'
        className={classes}
        onClick={handleClick}
        onMouseDown={(e) => e.preventDefault()}
      >
        {content}
      </Box>
    )

  if (link) {
    return (
      <Link
        href={link.href}
        target={link.target}
        underline={false}
        wrapChildren={false}
        onClick={(e) => e.stopPropagation()}
      >
        {element}
      </Link>
    )
  }

  return element
}
